import React from 'react';

/**
 * Text input with label, hint, error and optional leading icon.
 *
 * VENDORED CHANGE (accessibility): upstream renders the error message but does
 * not connect it to the input, so a screen reader announces the field as valid
 * and never reads the error. This version sets aria-invalid and wires
 * aria-describedby to the hint/error element. Belongs upstream.
 */
export function Input({ label, hint, error, leadingIcon, size = 'md', style, id, ...rest }) {
  const [focus, setFocus] = React.useState(false);
  const reactId = React.useId();
  const inputId = id || reactId;
  const describedById = `${inputId}-desc`;

  const heights = { sm: 36, md: 44, lg: 52 };
  const h = heights[size] || heights.md;
  const borderColor = error ? 'var(--y-danger)' : focus ? 'var(--accent-primary)' : 'var(--border-default)';
  const message = error || hint;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', ...style }}>
      {label && (
        <label
          htmlFor={inputId}
          style={{
            fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)',
            fontWeight: 'var(--fw-bold)', color: 'var(--text-strong)',
          }}
        >
          {label}
        </label>
      )}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', height: h,
          padding: '0 var(--sp-4)', background: 'var(--bg-surface)',
          border: `1.5px solid ${borderColor}`, borderRadius: 'var(--radius-sm)',
          boxShadow: focus ? '0 0 0 3px var(--focus-ring)' : 'none',
          transition: 'border-color var(--dur) var(--ease-out), box-shadow var(--dur) var(--ease-out)',
        }}
      >
        {leadingIcon && <span style={{ color: 'var(--text-muted)', display: 'flex' }}>{leadingIcon}</span>}
        <input
          id={inputId}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={message ? describedById : undefined}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          style={{
            flex: 1, border: 'none', outline: 'none', background: 'transparent',
            fontFamily: 'var(--font-body)', fontSize: 'var(--fs-body)',
            color: 'var(--text-strong)', minWidth: 0,
          }}
          {...rest}
        />
      </div>
      {message && (
        <span
          id={describedById}
          style={{
            fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)',
            color: error ? 'var(--y-danger)' : 'var(--text-muted)',
          }}
        >
          {message}
        </span>
      )}
    </div>
  );
}
